import { IconButton, Popconfirm, Stack } from "@altimateai/lego";
import {
  Alert,
  FormItem,
  FormMessage as FormFeedback,
  Tooltip,
  Typography,
} from "@altimateai/lego";
import { ContentCategory, Learning, learningSchema, PersonalizationScope } from "./types";
import { Collapse } from "../../shared";
import { Card, CardContent, CardHeader, Switch, Textarea } from "@altimateai/lego";
import dayjs from "dayjs";
import { ChevronDownIcon, ChevronUpIcon, DeleteIcon, EditIcon } from "../../assets/icons";
import { ApiHelper, useMutation } from "../api/index";
import { Button, NativeSelect } from "@altimateai/lego";
import { MouseEvent, SyntheticEvent, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

interface Props {
  learning: Learning;
  afterDelete: () => void;
  toggle: (id: string) => void;
  openId?: string | null;
}
const LearningRow = ({ learning, afterDelete, toggle, openId }: Props) => {
  const [isInEditMode, setIsInEditMode] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    watch,
    setValue,
  } = useForm({
    defaultValues: learning,
    resolver: zodResolver(learningSchema),
  });

  const values = watch();

  const onSubmit = async (data: Learning) => {
    await ApiHelper.post(`coach/training/${learning.train_doc_uid}`, data, {
      method: "PUT",
    });
    setIsInEditMode(false);
  };

  const deleteLearning = async () => {
    return await ApiHelper.post(
      `coach/training/${learning.train_doc_uid}`,
      {},
      { method: "DELETE" }
    );
  };

  const { loading, error, mutate } = useMutation({
    queryFn: deleteLearning,
    onSuccess: afterDelete,
  });

  const stopPropagation = (e?: SyntheticEvent) => {
    e?.stopPropagation();
  };

  const onConfirm = () => {
    stopPropagation();
    mutate();
  };

  const onEditCancel = (e: MouseEvent) => {
    stopPropagation(e);
    setIsInEditMode(false);
  };
  const onEditClick = (e: MouseEvent) => {
    stopPropagation(e);
    toggle(learning.train_doc_uid);
    setIsInEditMode(true);
  };

  const isOpen = openId === learning.train_doc_uid;

  return (
    <Card className={isOpen ? "al-border-blue-500" : ""}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardHeader>
          <Stack align="start">
            <div className="al-flex-1">
              <div>
                {isInEditMode ? (
                  <FormItem>
                    <Textarea
                      {...register("content")}
                      placeholder="Enter your coaching feedback here..."
                      aria-invalid={!!errors.content}
                      onClick={stopPropagation}
                      // @ts-ignore
                      style={{ fieldSizing: "content" }}
                    />
                    {errors.content ? <FormFeedback>{errors.content.message}</FormFeedback> : null}
                  </FormItem>
                ) : (
                  <Typography className={!isOpen ? "lines-2" : ""}>{values.content}</Typography>
                )}
              </div>

              <Stack>
                <dl>
                  <Stack>
                    <dt>Created on:</dt>
                    <dd>{dayjs(learning.createdDate).format("MMMM D, YYYY h:mm A")}</dd>
                  </Stack>
                  <Stack>
                    <dt>Updated on:</dt>
                    <dd>{dayjs(learning.updatedDate).format("MMMM D, YYYY h:mm A")} </dd>
                  </Stack>
                  <Stack>
                    <dt>Created by:</dt> <dd>{learning.display_name}</dd>
                  </Stack>
                </dl>
              </Stack>
            </div>
            <Stack className="align-items-top gap-0">
              <FormItem className="al-mt-2">
                <Tooltip
                  title={!values.isActive ? "Enable this learning" : "Disable this learning"}
                >
                  <span>
                    <Switch
                      id={`toggle-${learning.train_doc_uid}`}
                      checked={values.isActive}
                      onCheckedChange={checked => {
                        setValue("isActive", checked);
                        handleSubmit(onSubmit)();
                      }}
                    />
                  </span>
                </Tooltip>
              </FormItem>
              <Popconfirm
                title="Delete the task"
                description="Are you sure to delete this task?"
                onConfirm={onConfirm}
                onCancel={stopPropagation}
                okText="Yes"
                cancelText="No"
              >
                <IconButton
                  title="Delete this learning"
                  className="pt-1 pb-0"
                  disabled={loading}
                  onClick={stopPropagation}
                >
                  <DeleteIcon />
                </IconButton>
              </Popconfirm>
              {isInEditMode ? (
                <Stack className="align-items-baseline">
                  <Button onClick={onEditCancel} variant="outline" size="sm">
                    Cancel
                  </Button>
                  <Button size="sm" disabled={isSubmitting || !isValid} type="submit">
                    Save
                  </Button>
                </Stack>
              ) : (
                <IconButton title="Edit this learning" onClick={onEditClick}>
                  <EditIcon />
                </IconButton>
              )}

              <IconButton
                title={isOpen ? "Collapse" : "Expand"}
                onClick={() => toggle(learning.train_doc_uid)}
              >
                {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </IconButton>
            </Stack>
          </Stack>
        </CardHeader>
        <Collapse isOpen={isOpen}>
          <CardContent>
            {error && <Alert color="danger">{error.message}</Alert>}
            <Stack className="gap-4">
              <div>
                <strong>Category:</strong>{" "}
                {isInEditMode ? (
                  <NativeSelect
                    style={{ minWidth: 160 }}
                    options={Object.values(ContentCategory).map(c => ({
                      value: c,
                      label: c,
                    }))}
                    value={values.category}
                    onChange={(value: string) => {
                      setValue("category", value as ContentCategory);
                    }}
                  />
                ) : (
                  learning.category
                )}
              </div>
              <div>
                <strong>Task Label:</strong> {learning.taskLabel}
              </div>
              <div>
                <strong>Personalization Scope:</strong>{" "}
                {isInEditMode ? (
                  <NativeSelect
                    style={{ minWidth: 100 }}
                    options={Object.values(PersonalizationScope).map(c => ({
                      value: c,
                      label: c,
                    }))}
                    value={values.personalizationScope}
                    onChange={(value: string) => {
                      setValue("personalizationScope", value as PersonalizationScope);
                    }}
                  />
                ) : (
                  learning.personalizationScope
                )}
              </div>
              {learning.metadata &&
                Object.keys(learning.metadata).map(key => (
                  <div key={key}>
                    <strong>{key}:</strong> <>{learning.metadata?.[key] || ""}</>
                  </div>
                ))}
            </Stack>
          </CardContent>
        </Collapse>
      </form>
    </Card>
  );
};

export default LearningRow;
